import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from 'dayjs';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ExportExcel from '../../Components/ExportExcel';
import ImportExcel from '../../Components/ImportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  autoFocusAndSelect,
  handleCopy,
  mergeEditedGridRows,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';

function ProjectPerformanceSummary({
  name,
  rows,
  companies,
  organisations,
  dates,
  activeFilter,
  clients,
  projects,
  projecttypes,
  projectsubtypes,
  employees,
  selectedCompany,
  selectedOrganisation,
  selectedDates,
  orgLength,
  resetReportingState,
  onClickedAdd,
  onClickedDelete,
  getStateData,
  getClients,
  getEmployees,
  getProjects,
  getProjectTypes,
  getProjectSubTypes,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    ProjectNumber: 'All',
    DimProjectKey: 'All',
    DimDateKey: 'All',
    DimClientKey: 'All',
    DimEmployeeDirectorKey: 'All',
    DimEmployeeProjectManagerKey: 'All',
    DimEmployeeSupervisorKey: 'All',
    DimProjectTypeKey: 'All',
    DimProjectSubTypeKey: 'All',
    DimCompanyKey: 'All',
    DimOrganisationKey: 'All',
    TotalProjectValue: '',
    ActualFeesInvoicedMTD: '',
    ActualCostMTD: '',
    ActualHoursMTD: '',
    enabled: false,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);
  const [open, setOpen] = useState(false);
  const [exportAllData, setExportAllData] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [cinchImport, setCinchImport] = useState(false);

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'DimDateKey':
      case 'TotalProjectValue':
      case 'ActualFeesInvoicedMTD':
      case 'ActualCostMTD':
      case 'ActualProfitMTD':
      case 'ActualHoursMTD':
      case 'ProjectNumber':           
        return (a, b) => {
          return a[sortColumn] - b[sortColumn];
        };

      case 'DimCompanyKey':
        return (a, b) => {
          const aName = getCompanyNameById(a[sortColumn]);
          const bName = getCompanyNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimOrganisationKey':
        return (a, b) => {
          const aName = getOrganisationNameById(a[sortColumn]);
          const bName = getOrganisationNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimProjectKey':
        return (a, b) => {
          const aName = getProjectNameById(a[sortColumn]);
          const bName = getProjectNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
      
      case 'DimClientKey':
        return (a, b) => {
          const aName = getClientNameById(a[sortColumn]);
          const bName = getClientNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimEmployeeDirectorKey':
      case 'DimEmployeeProjectManagerKey':
      case 'DimEmployeeSupervisorKey':
        return (a, b) => {
          const aName = getEmployeeNameById(a[sortColumn]);
          const bName = getEmployeeNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimProjectTypeKey':
        return (a, b) => {
          const aName = getProjectTypeNameById(a[sortColumn]);
          const bName = getProjectTypeNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
        
      case 'DimProjectSubTypeKey':
        return (a, b) => {
          const aName = getProjectSubTypeNameById(a[sortColumn]);
          const bName = getProjectSubTypeNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    let startMonth = companies.find((com) => com.id === selectedCompany)
      ? companies.find((com) => com.id === selectedCompany).FiscalYearStartMonth
      : 7;
    let startDate, endDate;

    if (new Date().getMonth() >= startMonth - 1) {
      startDate = new Date().getFullYear() * 10000 + startMonth * 100 + 1;
      endDate =
        (new Date().getFullYear() + 1) * 10000 + (startMonth - 1) * 100 + 1;
    } else {
      startDate = (new Date().getFullYear() - 1) * 10000 + startMonth * 100 + 1;
      endDate = new Date().getFullYear() * 10000 + (startMonth - 1) * 100 + 1;
    }

    let filterRows = [];
    if (activeFilter) {
      filterRows = rows.filter(
        (row) => row.DimDateKey >= startDate && row.DimDateKey <= endDate,
      );
    } else filterRows = rows;

    filterRows.map(
      (row) =>
        (row.Project = projects.find(
          (project) => project[0] === row.DimProjectKey,
        )
          ? projects.find((project) => project[0] === row.DimProjectKey)[1]
          : ''),
    );

    filterRows.sort((a, b) => {
      if (a.DimDateKey === b.DimDateKey) {
        return a.Project.localeCompare(b.Project);
      }
      return b.DimDateKey - a.DimDateKey;
    });

    setTableRows(filterRows);
  }, [rows, activeFilter, companies, projects, selectedCompany]);

  const getCompanyNameById = (id) => {
    if (!id) return '';
    const company = companies?.find(company => company.id === id);
    return company ? company.CompanyName : '';
  };

  const getOrganisationNameById = (id) => {
    if (!id) return '';
    const organisation = organisations?.find(organisation => organisation.id === id);
    return organisation ? organisation.OrgName : '';
  };

  const getProjectNameById = (id) => {
    if (!id) return '';
    const project = projects?.find(project => project[0] === id);
    return project ? project[1] : '';
  };

  const getClientNameById = (id) => {
    if (!id) return '';
    const client = clients?.find(client => client[0] === id);
    return client ? client[1] : '';
  };

  const getEmployeeNameById = (id) => {
    if (!id) return '';
    const employee = employees?.find(employee => employee[0] === id);
    return employee ? employee[1] : '';
  };

  const getProjectTypeNameById = (id) => {
    if (!id) return '';
    const projectType = projecttypes?.find(type => type[0] === id);
    return projectType ? projectType[1] : '';
  };

  const getProjectSubTypeNameById = (id) => {
    if (!id) return '';
    const projectSubType = projectsubtypes?.find(type => type[0] === id);
    return projectSubType ? projectSubType[1] : '';
  };
  
  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'DimCompanyKey',
      'DimOrganisationKey',
      'ProjectNumber',
      'DimProjectKey',
      'DimDateKey',
      'DimClientKey',
      'DimEmployeeDirectorKey',
      'DimEmployeeProjectManagerKey',
      'DimEmployeeSupervisorKey',
      'DimProjectTypeKey',
      'DimProjectSubTypeKey',
      'ActualProfitMTD',
    ];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleClickDelete = () => {
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else {
      if (selectedRow) {
        let rowIndexes = [selectedRow.id];
        onClickedDelete(name, rowIndexes);
        setSelectedRow(null);
      }
    }
  };

  const handleSelectFrozenColumns = (event, name) => {
    event.stopPropagation();
    event.preventDefault();

    const currentIndex = frozenColumns.indexOf(name);
    const newColumns = [...frozenColumns];

    if (currentIndex === -1) {
      newColumns.push(name);
    } else {
      newColumns.splice(currentIndex, 1);
    }
    setFrozenColumns(newColumns);
  };

  const handleExcelImport = async (updated_data) => {
    if (cinchImport) {
      // check import data is valid
      const validData = updated_data.every(item => {
        return item.hasOwnProperty('ClientName') && (item.hasOwnProperty('ProjectId') || item.hasOwnProperty('ProjectID'));
      });
      if (!validData) {
        toast.warning('Please insert the valid data. Data must contain CompanyName, ClientName, and ProjectId.');
        return;
      }
      
      await ManageReportDataApi.importDataFromCinch(
        name,
        updated_data,
        selectedCompany,
        selectedOrganisation,
        selectedDates,
      )
        .then((res) => {
          if (res.status === 200) {
            getClients();
            getEmployees();
            getProjectTypes();
            getProjectSubTypes();
            getProjects();
            getStateData(res.summaries);
          } else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message));
    } else {
      // check import data is valid
      const validData = updated_data.every(item => {
        return item.hasOwnProperty('DimProjectKey') && item.hasOwnProperty('DimClientKey');
      });

      if (!validData) {
        toast.warning('Please insert the valid data. Data must contain DimProjectKey and DimClientKey.');
        return;
      }

      await ManageReportDataApi.importDataFromExcel(
        name,
        updated_data,
        selectedDates,
        selectedCompany,
        selectedOrganisation,
      ).then((res) => {
        if (res.status === 200) {
          getClients();
          getEmployees();
          getProjectTypes();
          getProjectSubTypes();
          getProjects();
          getStateData(res.data);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));
    }

    setCinchImport(false);
  };

  const handleGridRowsUpdated = async (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }

    if (
      field.column.key === 'TotalProjectValue' ||
      field.column.key === 'ActualFeesInvoicedMTD' ||
      field.column.key === 'ActualCostMTD' ||
      field.column.key === 'ActualProfitMTD' ||
      field.column.key === 'ActualHoursMTD'
    ) {
      if (isNaN(updated_data.updated[field.column.key])) {
        toast.warning('Please insert the number value.');
        return;
      }
    }

    if (field.column.key === 'DimProjectKey') {
      let updated = {};
      updated['DimProjectKey'] =
        updatedRows[field.indexes[0]][field.column.key];
      projects &&
        projects.forEach((cur) => {
          if (cur[0] === updatedRows[field.indexes[0]][field.column.key]) {
            updated['ProjectNumber'] = cur[11];
            updated['DimClientKey'] = cur[3];
            updated['DimEmployeeDirectorKey'] = cur[4];
            updated['DimEmployeeProjectManagerKey'] = cur[5];
            updated['DimEmployeeSupervisorKey'] = cur[6];
            updated['DimProjectTypeKey'] = cur[7];
            updated['DimProjectSubTypeKey'] = cur[8];
          }
        });

      updated_data.updated = Object.assign({}, updated);
    }

    if (field.column.key === 'ProjectNumber') {
      let updated = {};
      let findProject = projects.find(
        (project) =>
          project[11] === updatedRows[field.indexes[0]][field.column.key],
      );

      updated['DimProjectKey'] = findProject[0];
      projects &&
        projects.forEach((cur) => {
          if (cur[0] === findProject[0]) {
            updated['ProjectNumber'] =
              updatedRows[field.indexes[0]][field.column.key];
            updated['DimClientKey'] = cur[3];
            updated['DimEmployeeDirectorKey'] = cur[4];
            updated['DimEmployeeProjectManagerKey'] = cur[5];
            updated['DimEmployeeSupervisorKey'] = cur[6];
            updated['DimProjectTypeKey'] = cur[7];
            updated['DimProjectSubTypeKey'] = cur[8];
          }
        });

      updated_data.updated = Object.assign({}, updated);
    }

    if (selectedIds.size > 0) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedIds),
      ];
    }

    updatedRows.map((row) => {
      if (updated_data.rowIds.includes(row.id)) {
        Object.keys(updated_data.updated).forEach((fieldKey) => {
          row[fieldKey] = updated_data.updated[fieldKey];
        });
      }

      return row;
    });
    setTableRows((currentRows) =>
      mergeEditedGridRows(
        currentRows,
        updatedRows,
        updated_data.rowIds,
        updated_data.updated,
      ),
    );
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 500) {
          toast.error(res.message);
        } else {
          resetReportingState();
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const handleDateChange = (date, field) => {
    if (field === 'StartDate') {
      if (endDate) {
        if (
          new Date(date.$d).getTime() - 3600000 >
          new Date(endDate).getTime()
        ) {
          toast.warning(
            'Start date must be less than the end date!',
          );
          return;
        } else {
          setStartDate(date.$d);
        }
      } else setStartDate(date.$d);
    } else {
      if (startDate) {
        if (new Date(startDate).getTime() > new Date(date.$d).getTime()) {
          toast.warning(
            'End date must be greater than the start date!',
          );
          return;
        } else {
          setEndDate(date.$d);
        }
      } else setEndDate(date.$d);
    }
  };

  const handleExcelExport = (exportData) => {
    if (exportAllData) {
      if (exportData.length === 0)
        toast.warning('There is no export data!');
      setOpen(false);
    } else {
      if (startDate && endDate) {
        if (exportData.length === 0)
          toast.warning('There is no export data!');
        setOpen(false);
      } else {
        toast.warning(
          'Please select the start date and end date!',
        );
        return;
      }
    }
  };
  
  const projectPerformanceColumns = useMemo(() => {
    let project_performance_columns = [
      SelectColumn,
      {
        key: 'ProjectNumber',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Project Number</b>
            {frozenColumns.indexOf('ProjectNumber') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ProjectNumber')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ProjectNumber')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'ProjectNumber')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ProjectNumber',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 150,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('ProjectNumber') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => {
          let projectsByNumber = JSON.parse(JSON.stringify(projects)).sort(
            (a, b) => (a[11] < b[11] ? -1 : 1),
          );

          return (
            <FilterRenderer {...p}>
              {({ filters, ...rest }) => (
                <select
                  {...rest}
                  className="filter-form"
                  value={filters.ProjectNumber}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      ProjectNumber: e.target.value,
                    })
                  }
                  onKeyDown={selectStopPropagation}
                >
                  <option value="All">All</option>
                  {projectsByNumber &&
                    projectsByNumber.map((cur) => (
                      <option
                        key={cur[0]}
                        value={cur[11]}
                        style={{ display: cur[2] ? 'block' : 'none' }}
                      >
                        {cur[11]}
                      </option>
                    ))}
                </select>
              )}
            </FilterRenderer>
          );
        },
        renderCell: (props) => {
          let projectsByNumber = JSON.parse(JSON.stringify(projects)).sort(
            (a, b) => (a[11] < b[11] ? -1 : 1),
          );
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.ProjectNumber ? props.row.ProjectNumber : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    ProjectNumber: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {projectsByNumber &&
                projectsByNumber.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[11]}
                    style={{ display: cur[2] ? 'block' : 'none' }}
                  >
                    {cur[11]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimProjectKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Project Name</b>
            {frozenColumns.indexOf('DimProjectKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimProjectKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimProjectKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimProjectKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimProjectKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimProjectKey') !== -1 ? true : false,
        width: 175,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimProjectKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimProjectKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {clients &&
                  clients.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'block' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimProjectKey ? props.row.DimProjectKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimProjectKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {projects &&
                projects.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'block' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimDateKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Period</b>
            {frozenColumns.indexOf('DimDateKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimDateKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimDateKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimDateKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        frozen: frozenColumns.indexOf('DimDateKey') !== -1 ? true : false,
        width: 175,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimDateKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimDateKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {dates &&
                  dates.map((cur) => (
                    <option key={cur[0]} value={cur[0]}>
                      {cur[2] + ' ' + cur[3]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimDateKey ? props.row.DimDateKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimDateKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {dates &&
                dates.map((cur) => (
                  <MenuItem key={cur[0]} value={cur[0]}>
                    {cur[2] + ' ' + cur[3]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimClientKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Client</b>
            {frozenColumns.indexOf('DimClientKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimClientKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimClientKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimClientKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimClientKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        frozen: frozenColumns.indexOf('DimClientKey') !== -1 ? true : false,
        width: 175,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimClientKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimClientKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {clients &&
                  clients.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'block' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimClientKey ? props.row.DimClientKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimClientKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {clients &&
                clients.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'block' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeDirectorKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Director</b>
            {frozenColumns.indexOf('DimEmployeeDirectorKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeDirectorKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeDirectorKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeDirectorKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimEmployeeDirectorKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeDirectorKey') !== -1 ? true : false,
        width: 175,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeDirectorKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeDirectorKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeDirectorKey
                  ? props.row.DimEmployeeDirectorKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeDirectorKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeProjectManagerKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Project Manager</b>
            {frozenColumns.indexOf('DimEmployeeProjectManagerKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectManagerKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectManagerKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeProjectManagerKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimEmployeeProjectManagerKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeProjectManagerKey') !== -1
            ? true
            : false,
        width: 175,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeProjectManagerKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeProjectManagerKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeProjectManagerKey
                  ? props.row.DimEmployeeProjectManagerKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeProjectManagerKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeSupervisorKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Associate</b>
            {frozenColumns.indexOf('DimEmployeeSupervisorKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeSupervisorKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeSupervisorKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeSupervisorKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimEmployeeSupervisorKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeSupervisorKey') !== -1
            ? true
            : false,
        width: 175,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeSupervisorKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeSupervisorKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeSupervisorKey
                  ? props.row.DimEmployeeSupervisorKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeSupervisorKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimProjectTypeKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Project Type</b>
            {frozenColumns.indexOf('DimProjectTypeKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimProjectTypeKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimProjectTypeKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimProjectTypeKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimProjectTypeKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        resizable: true,
        sortable: true,
        width: 150,
        frozen:
          frozenColumns.indexOf('DimProjectTypeKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimProjectTypeKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimProjectTypeKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {projecttypes &&
                  projecttypes.map(
                    (cur) =>
                      cur[2] && (
                        <option key={cur[0]} value={cur[0]}>
                          {cur[1]}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimProjectTypeKey ? props.row.DimProjectTypeKey : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimProjectTypeKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {projecttypes &&
                projecttypes.map(
                  (cur) =>
                    cur[2] && (
                      <MenuItem key={cur[0]} value={cur[0]}>
                        {cur[1]}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      },
      {
        key: 'DimProjectSubTypeKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Project Sub Type</b>
            {frozenColumns.indexOf('DimProjectSubTypeKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimProjectSubTypeKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimProjectSubTypeKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimProjectSubTypeKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'DimProjectSubTypeKey')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        resizable: true,
        sortable: true,
        width: 160,
        frozen:
          frozenColumns.indexOf('DimProjectSubTypeKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimProjectSubTypeKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimProjectSubTypeKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {projectsubtypes &&
                  projectsubtypes.map(
                    (cur) =>
                      cur[2] && (
                        <option key={cur[0]} value={cur[0]}>
                          {cur[1]}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimProjectSubTypeKey
                  ? props.row.DimProjectSubTypeKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimProjectSubTypeKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {projectsubtypes &&
                projectsubtypes.map(
                  (cur) =>
                    cur[2] && (
                      <MenuItem key={cur[0]} value={cur[0]}>
                        {cur[1]}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      },
      {
        key: 'TotalProjectValue',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>TotalProjectValue</b>
            {frozenColumns.indexOf('TotalProjectValue') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'TotalProjectValue')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'TotalProjectValue')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'TotalProjectValue',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'TotalProjectValue',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 190,
        resizable: true,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.TotalProjectValue}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    TotalProjectValue: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('TotalProjectValue') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.TotalProjectValue}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                TotalProjectValue: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'ActualFeesInvoicedMTD',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>ActualFeesInvoicedMTD</b>
            {frozenColumns.indexOf('ActualFeesInvoicedMTD') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'ActualFeesInvoicedMTD')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'ActualFeesInvoicedMTD')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'ActualFeesInvoicedMTD',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ActualFeesInvoicedMTD',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 240,
        resizable: true,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ActualFeesInvoicedMTD}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ActualFeesInvoicedMTD: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen:
          frozenColumns.indexOf('ActualFeesInvoicedMTD') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.ActualFeesInvoicedMTD}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                ActualFeesInvoicedMTD: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'ActualCostMTD',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>ActualCostMTD</b>
            {frozenColumns.indexOf('ActualCostMTD') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ActualCostMTD')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ActualCostMTD')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'ActualCostMTD')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ActualCostMTD',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 165,
        resizable: true,
        sortable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ActualCostMTD}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ActualCostMTD: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('ActualCostMTD') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.ActualCostMTD}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                ActualCostMTD: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'ActualProfitMTD',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>ActualProfitMTD</b>
            {frozenColumns.indexOf('ActualProfitMTD') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ActualProfitMTD')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ActualProfitMTD')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        resizable: true,
        sortable: true,
        width: 160,
        frozen: frozenColumns.indexOf('ActualProfitMTD') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.ActualProfitMTD ?? 0}
            type="number"
            onChange={(e) =>
              props.onRowChange({ ...props.row, ActualProfitMTD: e.target.value })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'ActualHoursMTD',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>ActualHoursMTD</b>
            {frozenColumns.indexOf('ActualHoursMTD') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ActualHoursMTD')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ActualHoursMTD')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'ActualHoursMTD')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ActualHoursMTD',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 180,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ActualHoursMTD}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ActualHoursMTD: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('ActualHoursMTD') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.ActualHoursMTD}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                ActualHoursMTD: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
    ];

    if (orgLength.length > 1) {
      project_performance_columns.splice(2, 0, {
        key: 'DimOrganisationKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Department</b>
            {frozenColumns.indexOf('DimOrganisationKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        width: 205,
        resizable: true,
        frozen:
          frozenColumns.indexOf('DimOrganisationKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimOrganisationKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimOrganisationKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {organisations &&
                  organisations.map(
                    (cur) =>
                      p.row.DimCompanyKey === cur.DimCompanyKey &&
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.OrgName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimOrganisationKey != null &&
                props.row.DimOrganisationKey
              }
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimOrganisationKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {organisations &&
                organisations.map(
                  (cur) =>
                    props.row.DimCompanyKey === cur.DimCompanyKey &&
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.OrgName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    if (companies.length > 1) {
      project_performance_columns.splice(2, 0, {
        key: 'DimCompanyKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Company</b>
            {frozenColumns.indexOf('DimCompanyKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimCompanyKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimCompanyKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimCompanyKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimCompanyKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimCompanyKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {companies &&
                  companies.map(
                    (cur) =>
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.CompanyName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimCompanyKey != null && props.row.DimCompanyKey}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimCompanyKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {companies &&
                companies.map(
                  (cur) =>
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.CompanyName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    let frozenCols = project_performance_columns.filter(
      (col) => col.frozen === true,
    );
    let unfrozenCols = project_performance_columns.filter(
      (col) => col.frozen === false,
    );
    project_performance_columns = [...frozenCols, ...unfrozenCols];

    return project_performance_columns;
    // eslint-disable-next-line
  }, [
    tableRows,
    sortColumns,
    frozenColumns,
    companies,
    organisations,
    orgLength,
    projects,
    dates,
    clients,
    employees,
    projecttypes,
    projectsubtypes,
    handleSelectFrozenColumns,
  ]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.ProjectNumber !== 'All'
          ? // eslint-disable-next-line
            r.ProjectNumber == filters.ProjectNumber
          : true) &&
        (filters.DimProjectKey !== 'All'
          ? // eslint-disable-next-line
            r.DimProjectKey == filters.DimProjectKey
          : true) &&
        (filters.DimDateKey !== 'All'
          ? // eslint-disable-next-line
            r.DimDateKey == filters.DimDateKey
          : true) &&
        (filters.DimClientKey !== 'All'
          ? // eslint-disable-next-line
            r.DimClientKey == filters.DimClientKey
          : true) &&
        (filters.DimEmployeeDirectorKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeDirectorKey == filters.DimEmployeeDirectorKey
          : true) &&
        (filters.DimEmployeeProjectManagerKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeProjectManagerKey ==
            filters.DimEmployeeProjectManagerKey
          : true) &&
        (filters.DimEmployeeSupervisorKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeSupervisorKey == filters.DimEmployeeSupervisorKey
          : true) &&
        (filters.DimProjectTypeKey !== 'All'
          ? // eslint-disable-next-line
            r.DimProjectTypeKey == filters.DimProjectTypeKey
          : true) &&
        (filters.DimProjectSubTypeKey !== 'All'
          ? // eslint-disable-next-line
            r.DimProjectSubTypeKey == filters.DimProjectSubTypeKey
          : true) &&
        (filters.DimCompanyKey !== 'All'
          ? r.DimCompanyKey === filters.DimCompanyKey
          : true) &&
        (filters.DimOrganisationKey !== 'All'
          ? r.DimOrganisationKey === filters.DimOrganisationKey
          : true) &&
        (filters.TotalProjectValue
          ? r.TotalProjectValue?.toString().includes(
              filters.TotalProjectValue.toString(),
            )
          : true) &&
        (filters.ActualFeesInvoicedMTD
          ? r.ActualFeesInvoicedMTD?.toString().includes(
              filters.ActualFeesInvoicedMTD.toString(),
            )
          : true) &&
        (filters.ActualCostMTD
          ? r.ActualCostMTD?.toString().includes(
              filters.ActualCostMTD.toString(),
            )
          : true) &&
        (filters.ActualHoursMTD
          ? r.ActualHoursMTD?.toString().includes(
              filters.ActualHoursMTD.toString(),
            )
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);
  
  const exportData = useMemo(() => {
    if (exportAllData) {
      return [...tableRows];
    }
    
    if (startDate && endDate) {
      const start_date =
        new Date(startDate).getFullYear() * 10000 +
        (new Date(startDate).getMonth() + 1) * 100 +
        1;
      const end_date =
        new Date(endDate).getFullYear() * 10000 +
        (new Date(endDate).getMonth() + 1) * 100 +
        1;
      
      return tableRows.filter(
        (data) => data.DimDateKey >= start_date && data.DimDateKey <= end_date
      );
    }
    
    return [];
  }, [exportAllData, tableRows, startDate, endDate]);

  const exportValue = useMemo(() => {
    // Initialize export structure with all sheets
    const sheetDefinitions = {
      'Project Performance Summary': {
        columns: [
          { title: 'DimProjectKey', width: { wpx: 80 } },
          { title: 'ProjectID', width: { wpx: 80 } },
          { title: 'ProjectName', width: { wpx: 80 } },
          { title: 'DimCompanyKey', width: { wpx: 80 } },
          { title: 'CompanyCode', width: { wpx: 80 } },
          { title: 'CompanyName', width: { wpx: 80 } },
          { title: 'DimOrganisationKey', width: { wpx: 80 } },
          { title: 'OrgId', width: { wpx: 80 } },
          { title: 'OrgName', width: { wpx: 80 } },
          { title: 'DimDateKey', width: { wpx: 80 } },
          { title: 'DimClientKey', width: { wpx: 80 } },
          { title: 'ClientName', width: { wpx: 80 } },
          { title: 'DimEmployeeDirectorKey', width: { wpx: 80 } },
          { title: 'Director', width: { wpx: 80 } },
          { title: 'DimEmployeeProjectManagerKey', width: { wpx: 80 } },
          { title: 'ProjectManager', width: { wpx: 80 } },
          { title: 'DimEmployeeSupervisorKey', width: { wpx: 80 } },
          { title: 'Supervisor', width: { wpx: 80 } },
          { title: 'DimProjectTypeKey', width: { wpx: 80 } },
          { title: 'ProjectType', width: { wpx: 80 } },
          { title: 'DimProjectSubTypeKey', width: { wpx: 80 } },
          { title: 'ProjectSubType', width: { wpx: 80 } },
          { title: 'TotalProjectValue', width: { wpx: 80 } },
          { title: 'ActualFeesInvoicedMTD', width: { wpx: 80 } },
          { title: 'ActualCostMTD', width: { wpx: 80 } },
          { title: 'ActualProfitMTD', width: { wpx: 80 } },
          { title: 'ActualHoursMTD', width: { wpx: 80 } },
          { title: 'ForecastAmount', width: { wpx: 80 } },
        ],
        dataGenerator: (data) => {
          // Create lookup maps for faster access
          const companyMap = new Map(companies.map(c => [c.id, c]));
          const orgMap = new Map(organisations.map(o => [o.id, o]));
          const clientMap = new Map(clients.map(c => [c[0], c[1]]));
          const employeeMap = new Map(employees.map(e => [e[0], e[1]]));
          const projectTypeMap = new Map(projecttypes.map(p => [p[0], p[1]]));
          const projectSubTypeMap = new Map(projectsubtypes.map(p => [p[0], p[1]]));
          
          return data.map(item => [
            {
              value: item.DimProjectKey ? Number(item.DimProjectKey) : -1,
              style: { numFmt: '0' },
            },
            { value: item.ProjectNumber || '' },
            { value: item.Project || '' },
            {
              value: item.DimCompanyKey ? Number(item.DimCompanyKey) : -1,
              style: { numFmt: '0' },
            },
            {
              value: item.DimCompanyKey ? companyMap.get(Number(item.DimCompanyKey))?.CompanyCode || '' : '',
            },
            {
              value: item.DimCompanyKey ? companyMap.get(Number(item.DimCompanyKey))?.CompanyName || '' : '',
            },
            {
              value: item.DimOrganisationKey ? Number(item.DimOrganisationKey) : -1,
              style: { numFmt: '0' },
            },
            {
              value: item.DimOrganisationKey ? orgMap.get(Number(item.DimOrganisationKey))?.OrgId || '' : '',
            },
            {
              value: item.DimOrganisationKey ? orgMap.get(Number(item.DimOrganisationKey))?.OrgName || '' : '',
            },
            {
              value: item.DimDateKey ? Number(item.DimDateKey) : -1,
              style: { numFmt: '0' },
            },
            {
              value: item.DimClientKey ? Number(item.DimClientKey) : -1,
              style: { numFmt: '0' },
            },
            {
              value: item.DimClientKey ? clientMap.get(Number(item.DimClientKey)) || '' : '',
            },
            {
              value: item.DimEmployeeDirectorKey ? Number(item.DimEmployeeDirectorKey) : -1,
              style: { numFmt: '0' },
            },
            {
              value: item.DimEmployeeDirectorKey ? employeeMap.get(Number(item.DimEmployeeDirectorKey)) || '' : '',
            },
            {
              value: item.DimEmployeeProjectManagerKey ? Number(item.DimEmployeeProjectManagerKey) : -1,
              style: { numFmt: '0' },
            },
            {
              value: item.DimEmployeeProjectManagerKey ? employeeMap.get(Number(item.DimEmployeeProjectManagerKey)) || '' : '',
            },
            {
              value: item.DimEmployeeSupervisorKey ? Number(item.DimEmployeeSupervisorKey) : -1,
              style: { numFmt: '0' },
            },
            {
              value: item.DimEmployeeSupervisorKey ? employeeMap.get(Number(item.DimEmployeeSupervisorKey)) || '' : '',
            },
            {
              value: item.DimProjectTypeKey ? Number(item.DimProjectTypeKey) : -1,
              style: { numFmt: '0' },
            },
            {
              value: item.DimProjectTypeKey ? projectTypeMap.get(Number(item.DimProjectTypeKey)) || '' : '',
            },
            {
              value: item.DimProjectSubTypeKey ? Number(item.DimProjectSubTypeKey) : -1,
              style: { numFmt: '0' },
            },
            {
              value: item.DimProjectSubTypeKey ? projectSubTypeMap.get(Number(item.DimProjectSubTypeKey)) || '' : '',
            },
            {
              value: item.TotalProjectValue ? Number(item.TotalProjectValue) : 0,
              style: { numFmt: '0.00' },
            },
            {
              value: item.ActualFeesInvoicedMTD ? Number(item.ActualFeesInvoicedMTD) : 0,
              style: { numFmt: '0.00' },
            },
            {
              value: item.ActualCostMTD ? Number(item.ActualCostMTD) : 0,
              style: { numFmt: '0.00' },
            },
            {
              value: item.ActualProfitMTD ? Number(item.ActualProfitMTD) : 0,
              style: { numFmt: '0.00' },
            },
            {
              value: item.ActualHoursMTD ? Number(item.ActualHoursMTD) : 0,
              style: { numFmt: '0.00' },
            },
            {
              value: item.ForecastAmount ? Number(item.ForecastAmount) : 0,
              style: { numFmt: '0.00' },
            },
          ]);
        }
      },
      'Project': {
        columns: [
          { title: 'id', width: { wpx: 80 } },
          { title: 'Id', width: { wpx: 80 } },
          { title: 'Name', width: { wpx: 80 } },
        ],
        dataGenerator: () => projects?.map(cur => [
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[11] },
          { value: cur[1] },
        ]) || []
      },
      'Company': {
        columns: [
          { title: 'id', width: { wpx: 80 } },
          { title: 'CompanyName', width: { wpx: 80 } },
        ],
        dataGenerator: () => companies?.map(cur => [
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.CompanyName },
        ]) || []
      },
      'Organisation': {
        columns: [
          { title: 'id', width: { wpx: 80 } },
          { title: 'OrgName', width: { wpx: 80 } },
        ],
        dataGenerator: () => organisations?.map(cur => [
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.OrgName },
        ]) || []
      },
      'Date': {
        columns: [
          { title: 'id', width: { wpx: 80 } },
          { title: 'Date', width: { wpx: 80 } },
        ],
        dataGenerator: () => dates?.map(cur => [
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[2] + ' ' + cur[3] },
        ]) || []
      },
      'Client': {
        columns: [
          { title: 'id', width: { wpx: 80 } },
          { title: 'Name', width: { wpx: 80 } },
        ],
        dataGenerator: () => clients?.map(cur => [
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]) || []
      },
      'Employee': {
        columns: [
          { title: 'id', width: { wpx: 80 } },
          { title: 'Name', width: { wpx: 80 } },
        ],
        dataGenerator: () => employees?.map(cur => [
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]) || []
      },
      'Project Type': {
        columns: [
          { title: 'id', width: { wpx: 80 } },
          { title: 'Name', width: { wpx: 80 } },
        ],
        dataGenerator: () => projecttypes?.map(cur => [
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]) || []
      },
      'Project Sub Type': {
        columns: [
          { title: 'id', width: { wpx: 80 } },
          { title: 'Name', width: { wpx: 80 } },
        ],
        dataGenerator: () => projectsubtypes?.map(cur => [
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]) || []
      }
    };

    // Generate the final export value object
    const result = {};
    Object.entries(sheetDefinitions).forEach(([sheetName, definition]) => {
      result[sheetName] = {
        columns: definition.columns,
        data: sheetName === 'Project Performance Summary' 
          ? definition.dataGenerator(exportData)
          : definition.dataGenerator()
      };
    });

    return result;
  }, [
    exportData,
    projects,
    companies,
    organisations,
    dates,
    employees,
    clients,
    projecttypes,
    projectsubtypes,
  ]);
  
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <ImportExcel name="r4d" onImport={handleExcelImport} />
        <ImportExcel
          name="cinch"
          onImport={handleExcelImport}
          setCinchFlag={() => setCinchImport(true)}
        />
        <IconButton onClick={() => setOpen(true)}>
          <GetAppIcon />
        </IconButton>
        <Button
          onClick={() =>
            selectedDates.length === 1 ? onClickedAdd(name, 'auto') : null
          }
          variant="outlined"
          color="success"
        >
          Auto Add
        </Button>
        <IconButton
          onClick={() =>
            selectedDates.length === 1 ? onClickedAdd(name) : null
          }
        >
          <AddIcon />
        </IconButton>
        <IconButton onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>

      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={projectPerformanceColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}          
        />
      </FilterContext.Provider>

      <Dialog
        open={open}
        maxWidth="sm"
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Select Date Range'}</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={exportAllData}
                onChange={(event) => setExportAllData(event.target.checked)}
                name="exportAllData"
                color="success"
              />
            }
            label="Export all data"
          />
          {!exportAllData ? (
            <Grid container spacing={1}>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="Start Date"
                      variant="inline"
                      value={dayjs(startDate)}
                      onChange={(event) => handleDateChange(event, 'StartDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      label="End Date"
                      variant="inline"
                      value={dayjs(endDate)}
                      onChange={(event) => handleDateChange(event, 'EndDate')}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            color="error"
            size="small"
          >
            Cancel
          </Button>
          {exportData.length > 0 ? (
            <div onClick={() => setOpen(false)}>
              <ExportExcel
                filename="Project Performance Summary"
                value={exportValue}
              />
            </div>
          ) : (
            <Button
              onClick={() => handleExcelExport(exportData)}
              variant="outlined"
              color="success"
              size="small"
            >
              Export
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

ProjectPerformanceSummary.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  organisations: PropTypes.array.isRequired,
  dates: PropTypes.array.isRequired,
  activeFilter: PropTypes.bool.isRequired,
  clients: PropTypes.array.isRequired,
  projects: PropTypes.array.isRequired,
  projecttypes: PropTypes.array.isRequired,
  projectsubtypes: PropTypes.array.isRequired,
  employees: PropTypes.array.isRequired,
  selectedDates: PropTypes.array.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  orgLength: PropTypes.array.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedUpdate: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
  getClients: PropTypes.func.isRequired,
  getEmployees: PropTypes.func.isRequired,
  getProjects: PropTypes.func.isRequired,
  getProjectTypes: PropTypes.func.isRequired,
  getProjectSubTypes: PropTypes.func.isRequired,
};

export default ProjectPerformanceSummary;
